/**
 * Outlook event color rendering — the web half of the Outlook color scheme.
 *
 * Outlook has no per-event color id; color is conveyed through `event.categories`
 * resolved against the mailbox master category list to a preset
 * (`preset0`-`preset24`). The backend resolves that and stores the preset string
 * on `CalendarEvent.colorId`. This file owns only what the client needs to
 * *render* and *highlight* that preset; the backend's own preset logic (names,
 * Graph writes) lives in `api/src/clients/outlookCategoryColors.ts`. These were
 * deliberately split rather than shared through `types/`, which is reserved for
 * web↔API wire types.
 *
 * Hex values aren't published by Microsoft (they vary by client); these match
 * the modern Outlook desktop / OWA palette so our calendar looks like Outlook.
 * Preset → name mapping is Microsoft's documented table:
 * https://learn.microsoft.com/en-us/graph/api/resources/outlookcategory
 */
const OUTLOOK_PRESET_HEX: Record<string, string> = {
  preset0: "#F07D88", // Red
  preset1: "#FF8C00", // Orange
  preset2: "#FECB6F", // Brown
  preset3: "#FFF100", // Yellow
  preset4: "#5FBE7D", // Green
  preset5: "#33BAB1", // Teal
  preset6: "#A3B367", // Olive
  preset7: "#55ABE5", // Blue
  preset8: "#A895E2", // Purple
  preset9: "#E48BB5", // Cranberry
  preset10: "#B9C0CB", // Steel
  preset11: "#4C596E", // DarkSteel
  preset12: "#ABABAB", // Gray
  preset13: "#666666", // DarkGray
  preset14: "#474747", // Black
  preset15: "#910A19", // DarkRed
  preset16: "#CE4B28", // DarkOrange
  preset17: "#A47332", // DarkBrown
  preset18: "#B0A923", // DarkYellow
  preset19: "#026802", // DarkGreen
  preset20: "#1C6367", // DarkTeal
  preset21: "#5C6A22", // DarkOlive
  preset22: "#254069", // DarkBlue
  preset23: "#562685", // DarkPurple
  preset24: "#80275D", // DarkCranberry
};

/** Resolve an Outlook preset (`"preset3"`) to its render hex, or undefined. */
export const getOutlookPresetHex = (preset: string | undefined): string | undefined =>
  preset ? OUTLOOK_PRESET_HEX[preset] : undefined;

/**
 * Maps an Outlook preset back to the unified picker's Google swatch id, so an
 * Outlook event whose stored preset is one of these 11 highlights the matching
 * swatch. Inverse of the backend's write mapping in
 * `api/src/clients/outlookCategoryColors.ts` (`GOOGLE_COLOR_ID_TO_OUTLOOK_PRESET`),
 * and duplicated in native at `native/src/features/calendar/colors/`. All three
 * live in separate packages and can't import each other, so an edit here has to
 * be mirrored in the other two — each package's full-pairing golden only fails
 * for the package that was edited.
 */
const OUTLOOK_PRESET_TO_GOOGLE_COLOR_ID: Record<string, string> = {
  preset8: "1", // Purple     → Lavender
  preset4: "2", // Green      → Sage
  preset23: "3", // DarkPurple → Grape
  preset0: "4", // Red        → Flamingo
  preset3: "5", // Yellow     → Banana
  preset1: "6", // Orange     → Tangerine
  preset7: "7", // Blue       → Peacock
  preset13: "8", // DarkGray   → Graphite
  preset22: "9", // DarkBlue   → Blueberry
  preset19: "10", // DarkGreen  → Basil
  preset15: "11", // DarkRed    → Tomato
};

/** Map an Outlook preset to the unified Google swatch id, if one matches. */
export const outlookPresetToGoogleColorId = (preset: string | undefined): string | undefined =>
  preset ? OUTLOOK_PRESET_TO_GOOGLE_COLOR_ID[preset] : undefined;
